"use client";

import { Logo } from "@scott/ui-d2c";
import type { BrandConfig } from "@scott/ui-d2c/types";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { processLoginSubmission } from "@/app/[country]/[lang]/(auth)/login/actions/actions";
import { useAcceleratorCartCount } from "@/hooks/use-accelerator-cart-count";
import { useNavigationCartItems } from "@/hooks/use-navigation-cart-items";
import {
  MainNavigation,
  type MainNavigationProps,
} from "@/scott/ui/layout/main-navigation/main-navigation";
import type { MainNavigationAction } from "@/scott/ui/layout/main-navigation/main-navigation.types";
import { MobileNavigationBar } from "@/scott/ui/layout/main-navigation/mobile-navigation-bar/mobile-navigation-bar";
import { NavigationCart } from "@/scott/ui/layout/main-navigation/navigation-cart/navigation-cart";
import {
  NavigationLogin,
  type NavigationLoginCredentials,
} from "@/scott/ui/layout/main-navigation/navigation-login/navigation-login";
import { NavigationSearch } from "@/scott/ui/layout/main-navigation/navigation-search/navigation-search";
import { PRODUCT_ADDED_EVENT } from "@/utils/accelerator-cart.utils";
import {
  fetchSuggestionsFromApi,
  getSuggestionHref,
} from "@/utils/storefront-search.utils";
import {
  NavigationCartIcon,
  NavigationProfileIcon,
  NavigationSearchIcon,
  // TODO: Restore once the wishlist goes live.
  // NavigationWishlistIcon,
} from "./navigation-icons";

const ERROR_ACCOUNT_DISABLED = 403_041;
const ERROR_INVALID_CREDENTIALS = 403_042;
const ERROR_ACCOUNT_BLOCKED = 403_120;
/** Same budget as the login page's password step. */
const LOGIN_ATTEMPTS = 5;

interface ContentfulNavigationLinks {
  account: string;
  cart: string;
  checkout: string;
  forgotPassword: string;
  register: string;
  search: string;
  // wishlist: string;
}

interface ContentfulNavigationProps {
  brand: BrandConfig;
  country: string;
  homeHref: string;
  isEcommerce: boolean;
  isLoggedIn: boolean;
  items: MainNavigationProps["items"];
  lang: string;
  links: ContentfulNavigationLinks;
  serviceLinks?: MainNavigationProps["serviceLinks"];
  siteUid: string;
  teaser?: MainNavigationProps["teaser"];
  microcopies: MainNavigationProps["microcopies"] & {
    openMenu: string;
    closeMenu: string;
  };
}

/** Coordinates the shared desktop navigation and persistent mobile bar. */
export function ContentfulNavigation(
  props: Readonly<ContentfulNavigationProps>
) {
  const [state, setState] = useState<{
    isMobileMenuOpen: boolean;
    openActionId: string | null;
    /** The cart drawer was opened by adding a product, not by the icon. */
    isCartOpenedByAdd: boolean;
  }>({ isMobileMenuOpen: false, openActionId: null, isCartOpenedByAdd: false });
  const t = useTranslations("passwordPage");
  const tNavigation = useTranslations("header.navigation");
  const loginAttemptsRef = useRef(LOGIN_ATTEMPTS);
  // Both refresh on the same cart updates, so the count and the drawer agree.
  const cartCount = useAcceleratorCartCount(
    props.country,
    props.lang,
    props.isEcommerce
  );
  const cartItems = useNavigationCartItems(
    props.country,
    props.lang,
    props.isEcommerce && state.openActionId === "cart"
  );
  const logo = (
    <Logo brand={props.brand} url={props.homeHref} variant="medium" />
  );

  // Shows the cart drawer after a product was added on this page; opening it
  // loads the updated entries. A rejected add only refreshes the count.
  useEffect(() => {
    if (!props.isEcommerce) {
      return;
    }

    function handleProductAdded() {
      setState((current) => ({
        ...current,
        openActionId: "cart",
        isCartOpenedByAdd: true,
      }));
    }

    window.addEventListener(PRODUCT_ADDED_EVENT, handleProductAdded);
    return () =>
      window.removeEventListener(PRODUCT_ADDED_EVENT, handleProductAdded);
  }, [props.isEcommerce]);

  /** Keeps the desktop drawer and mobile bar on the same open state. */
  function setIsMobileMenuOpen(isMobileMenuOpen: boolean) {
    setState((current) => ({ ...current, isMobileMenuOpen }));
  }

  function setOpenActionId(openActionId: string | null) {
    setState((current) => ({
      ...current,
      openActionId,
      isCartOpenedByAdd: false,
    }));
  }

  async function fetchSuggestions(query: string, signal: AbortSignal) {
    // Searches the page's own market, which getBaseUrl() fixes to ch/fr locally.
    const baseUrl = `${window.location.origin}${props.homeHref}/`;
    const products = await fetchSuggestionsFromApi(baseUrl, query, signal);
    return products.map((product) => ({
      id: product.code,
      label: product.name,
      href: getSuggestionHref(props.homeHref, product.url),
    }));
  }

  async function login(credentials: NavigationLoginCredentials) {
    const { attempts, response } = await processLoginSubmission(
      { attempts: loginAttemptsRef.current, response: undefined },
      credentials
    );
    loginAttemptsRef.current = attempts;

    if (response?.errorCode === 0 && response.UID) {
      // The session cookies are set; reload so the whole page sees the login.
      window.location.reload();
      return {};
    }

    const stripLinks = { link: (chunks: string) => chunks };
    switch (response?.errorCode) {
      case ERROR_ACCOUNT_DISABLED:
        return { error: t("errorMessages.accountDisabled") };
      case ERROR_ACCOUNT_BLOCKED:
        return { error: t.markup("errorMessages.accountBlocked", stripLinks) };
      case ERROR_INVALID_CREDENTIALS:
        return { error: t("errorMessages.invalidLogin", { attempts }) };
      default:
        return { error: t.markup("errorMessages.genericError", stripLinks) };
    }
  }

  const searchMicrocopies = {
    label: tNavigation("search.label"),
    placeholder: tNavigation("search.placeholder"),
    submit: tNavigation("search.submit"),
    clear: tNavigation("search.clear"),
    suggestions: tNavigation("search.suggestions"),
    loading: tNavigation("search.loading"),
    noResults: tNavigation("search.noResults"),
    // Raw, as the search panel fills in `{count}` itself.
    resultCount: tNavigation.raw("search.resultCount") as string,
    showAll: tNavigation("search.showAll"),
  };

  const cartMicrocopies = {
    quantity: tNavigation("cart.quantity"),
    unitPrice: tNavigation("cart.unitPrice"),
    total: tNavigation("cart.total"),
    viewCart: tNavigation("cart.viewCart"),
    continueShopping: tNavigation("cart.continueShopping"),
    checkout: tNavigation("cart.checkout"),
  };

  const loginMicrocopies = {
    title: tNavigation("login.title"),
    existingCustomer: tNavigation("login.existingCustomer"),
    email: tNavigation("login.email"),
    password: tNavigation("login.password"),
    showPassword: tNavigation("login.showPassword"),
    hidePassword: tNavigation("login.hidePassword"),
    forgotPassword: tNavigation("login.forgotPassword"),
    submit: tNavigation("login.submit"),
    newCustomer: tNavigation("login.newCustomer"),
    register: tNavigation("login.register"),
    genericError: tNavigation("login.genericError"),
  };

  const accountAction: MainNavigationAction = props.isLoggedIn
    ? {
        id: "account",
        label: tNavigation("actions.account"),
        href: props.links.account,
        icon: <NavigationProfileIcon />,
      }
    : {
        id: "account",
        label: tNavigation("actions.account"),
        icon: <NavigationProfileIcon />,
        panel: () => (
          <NavigationLogin
            forgotPasswordHref={props.links.forgotPassword}
            microcopies={loginMicrocopies}
            onSubmit={login}
            registerHref={props.links.register}
          />
        ),
      };

  const actions: MainNavigationAction[] = [
    {
      id: "search",
      label: tNavigation("actions.search"),
      icon: <NavigationSearchIcon />,
      panel: () => (
        <NavigationSearch
          action={props.links.search}
          fetchSuggestions={fetchSuggestions}
          microcopies={searchMicrocopies}
          queryParam="text"
        />
      ),
    },
    // TODO: Restore once the wishlist goes live.
    // {
    //   id: "wishlist",
    //   label: tNavigation("actions.wishlist"),
    //   href: props.links.wishlist,
    //   icon: <NavigationWishlistIcon />,
    // },
    // Non-e-commerce countries have no cart and no customer accounts.
    ...(props.isEcommerce
      ? [
          {
            id: "cart",
            // No href: the icon toggles the drawer, which links to the cart page.
            label: tNavigation("actions.cart"),
            count: cartCount,
            icon: <NavigationCartIcon />,
            panel: (close) => (
              <NavigationCart
                checkoutHref={props.links.checkout}
                items={cartItems}
                microcopies={cartMicrocopies}
                onContinueShopping={close}
                title={
                  state.isCartOpenedByAdd
                    ? tNavigation("cart.productAdded")
                    : tNavigation("cart.title")
                }
                viewCartHref={props.links.cart}
              />
            ),
          } satisfies MainNavigationAction,
          accountAction,
        ]
      : []),
  ];

  return (
    <>
      <MainNavigation
        actions={actions}
        items={props.items}
        logo={logo}
        microcopies={props.microcopies}
        mobileMenuOpen={state.isMobileMenuOpen}
        onMobileMenuOpenChange={setIsMobileMenuOpen}
        onOpenActionIdChange={setOpenActionId}
        openActionId={state.openActionId}
        serviceLinks={props.serviceLinks}
        teaser={props.teaser}
      />
      <MobileNavigationBar
        // The mobile design orders the icons the other way round.
        actions={[...actions].reverse()}
        isMenuOpen={state.isMobileMenuOpen}
        logo={logo}
        microcopies={props.microcopies}
        onMenuToggle={() => setIsMobileMenuOpen(!state.isMobileMenuOpen)}
        onOpenActionIdChange={setOpenActionId}
        openActionId={state.openActionId}
      />
    </>
  );
}
